import React from 'react'
import { ClipboardList, Package, Mail, Users, TrendingUp, DollarSign } from 'lucide-react'

const features = [
  {
    icon: ClipboardList,
    title: 'Order Management',
    description: 'Real-time order tracking, customer management, and status updates. Keep track of every orders.',
    color: 'from-cyan-500 to-blue-500'
  },
  {
    icon: Package,
    title: 'Inventory Control',
    description: 'Track supplies, manage stock levels, and get restock alerts. Never run out of essential items again.',
    color: 'from-blue-500 to-indigo-500'
  },
  {
    icon: Mail,
    title: 'Customer Campaigns',
    description: 'Run loyalty programs, email campaigns, and engage customers. Build lasting relationships with automated marketing.',
    color: 'from-indigo-500 to-purple-500'
  },
  {
    icon: Users,
    title: 'Staff Management',
    description: 'Track attendance, manage payroll, and monitor performance. Streamline your workforce management.',
    color: 'from-purple-500 to-pink-500'
  },
  {
    icon: TrendingUp,
    title: 'Business Analytics',
    description: 'Sales reports, customer insights, and performance metrics. Make data-driven decisions for growth.',
    color: 'from-pink-500 to-rose-500'
  },
  {
    icon: DollarSign,
    title: 'Expense Tracking',
    description: 'Monitor expenses, manage budgets, and track profitability. Keep your finances organized and transparent.',
    color: 'from-rose-500 to-orange-500'
  }
]

export default function Features() {
  return (
    <section id="features" className="py-20 bg-white dark:bg-gray-900 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Everything You Need to
            <span className="gradient-text"> Succeed</span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Powerful features designed specifically for laundry shop owners. Manage every aspect of your business from one platform.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className="group relative bg-white dark:bg-gray-700 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100 dark:border-gray-600"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Icon with Gradient Background */}
              <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <feature.icon className="w-8 h-8 text-white" />
              </div>

              {/* Title */}
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                {feature.title}
              </h3>

              {/* Description */}
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                {feature.description}
              </p>

              {/* Hover Effect Border */}
              <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300 pointer-events-none`} />
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            All features included for only P***/month
          </p>
          <a
            href="https://app.laundrymate.shop"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95"
          >
            Get Started Now
          </a>
        </div>
      </div>
    </section>
  )
}
